import { useEffect, useState } from "react";
import { useARRCode } from "../context/ARRLevelContext";
import { axiosPost } from "../../../utils/AxiosApi";
import { checkWidgetAdminOrNot, URL } from "../../../utils/Constants";
import swal from "sweetalert";

export const useYearLevelByAssessmentSubjectConfig = (widgetName) => {
  const [yearLoading, setYearLoading] = useState(false);
  const [yearLevel, setYearLevel] = useState([]);
  const { arrLevel } = useARRCode();

  const getYearLevelByAssessmentSubjectConfig = () => {
    setYearLoading(true);
    axiosPost(
      URL.getYearLevelByAssessmentSubjectConfig,
      {
        assessmentModuleCode: arrLevel,
      },
      (response) => {
        if (response.status === 200) {
          const data = response?.data?.data;
          setYearLevel(data);
        }
        setYearLoading(false);
      },
      (error) => {
        setYearLoading(false);
      },
    );
  };

  const getYearLevelByTeacher = () => {
    setYearLoading(true);
    axiosPost(
      URL.selectYearLevelByTeacher,
      {
        code: arrLevel,
      },
      (response) => {
        if (response.status === 200) {
          const data = response?.data?.data;
          setYearLevel(data);
        }
        setYearLoading(false);
      },
      (error) => {
        setYearLoading(false);
      },
    );
  };

  useEffect(() => {
    if (checkWidgetAdminOrNot(widgetName)) {
      getYearLevelByAssessmentSubjectConfig();
    } else {
      getYearLevelByTeacher();
    }
  }, [arrLevel]);

  return { yearLoading, yearLevel };
};

export const useAssessmentWithScaleOrComment = () => {
  const [assessments, setAssessments] = useState([]);
  const [assessmentsLoading, setAssessmentsLoading] = useState(false);

  const getAssessmentWithScaleOrComment = (params) => {
    setAssessmentsLoading(true);
    axiosPost(
      URL.getAssessmentWithScaleOrComment,
      params,
      (response) => {
        if (response.status === 200) {
          const data = response?.data?.data;
          const processedData = data?.map((item) => ({
            educationLevelDivisionId: item?.educationLevelDivisionId,
            label: item?.assessmentName,
            value: item?.id,
          }));
          setAssessments(processedData);
        }
        setAssessmentsLoading(false);
      },
      (error) => {
        setAssessmentsLoading(false);
      },
    );
  };

  return { assessments, assessmentsLoading, getAssessmentWithScaleOrComment };
};

export const useSubjectWithScaleOrComment = () => {
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(false);

  const getSubjectWithScaleOrComment = (params) => {
    setLoading(true);
    axiosPost(
      URL.getSubjectWithScaleOrComment,
      params,
      (response) => {
        if (response.status === 200) {
          const data = response?.data?.data;
          setSubjects(data);
        }
        setLoading(false);
      },
      (error) => {
        setLoading(false);
      },
    );
  };

  return { loading, subjects, getSubjectWithScaleOrComment };
};

export const useDataForRecording = () => {
  const [subjects, setSubjects] = useState([]);
  const [fetching, setFetching] = useState(false);

  const getDataForRecording = (params) => {
    setFetching(true);
    axiosPost(
      URL.getDataForRecording,
      params,
      (response) => {
        if (response.status === 200) {
          const data = response?.data?.data;
          setSubjects(data);
        }
        setFetching(false);
      },
      (error) => {
        setFetching(false);
      },
    );
  };

  return { fetching, subjects, setSubjects, getDataForRecording };
};

export const useStudentSubjectAssessments = (getDataForRecording) => {
  const [loading, setLoading] = useState({
    insert: false,
    finalize: false,
  });

  const handleGetDataForRecording = (payload) => {
    const params = {
      educationLevelDivisionId: payload?.educationLevelDivisionId,
      educationLevelId: payload.educationLevelId,
      yearLevelId: payload.yearLevelId,
      classroomId: payload.classroomId,
      assessmentId: payload?.assessmentId,
    };
    getDataForRecording(params);
  };

  const insertMultipleStudentSubjectAssessments = (
    params,
    subjectId,
    finalized,
  ) => {
    setLoading((prev) => ({ ...prev, insert: true }));
    axiosPost(
      URL.insertMultipleStudentSubjectAssessments,
      params,
      (response) => {
        if (response.status === 200) {
          setLoading((prev) => ({ ...prev, insert: false }));

          if (subjectId) {
            updateStudentSubjectAssessmentIsFinalized(
              params,
              subjectId,
              finalized,
            );

            return;
          }

          swal(
            "Success",
            response?.data?.message || "Successfully saved assessment record",
          );
          handleGetDataForRecording(params);
        }
      },
      (error) => {
        setLoading((prev) => ({ ...prev, insert: false }));
        swal(
          "Oops",
          error?.response?.data?.message || "Failed to save assessment record",
        );
      },
    );
  };

  const updateStudentSubjectAssessmentIsFinalized = (
    params,
    subjectId,
    finalized,
  ) => {
    const payload = {
      educationLevelDivisionId: params?.educationLevelDivisionId,
      educationLevelId: params.educationLevelId,
      yearLevelId: params.yearLevelId,
      classroomId: params.classroomId,
      assessmentId: params?.assessmentId,
      subjectId: subjectId,
      isFinalized: finalized ? false : true,
    };
    setLoading((prev) => ({ ...prev, finalize: true }));
    axiosPost(
      URL.updateStudentSubjectAssessmentIsFinalized,
      payload,
      (response) => {
        if (response.status === 200) {
          swal(
            "Success",
            `Successfully ${finalized ? "unfinalized" : "finalize"} assessment record`,
          );
          handleGetDataForRecording(payload);
        }
        setLoading((prev) => ({ ...prev, finalize: false }));
      },
      (error) => {
        setLoading((prev) => ({ ...prev, finalize: false }));
        swal(
          "Oops",
          error?.response?.data?.message ||
            `Failed to ${finalized ? "unfinalized" : "finalize"} assessment record`,
        );
      },
    );
  };

  return {
    loading,
    insertMultipleStudentSubjectAssessments,
    updateStudentSubjectAssessmentIsFinalized,
  };
};
